import { useTranslation } from 'react-i18next';
import { Button } from '../../Fundamentals/Button';
import Drawer from '../../Fundamentals/Drawer/Drawer';
import EmptyState from '../../Fundamentals/EmptyState/EmptyState';
import PackageStatusBadge from './PackageStatusBadge';
import dateUtils from '../../../Utils/dateUtils';
import type { NumberedVersion } from './versionUtils';

interface PackageVersionHistoryDrawerProps {
    packageName: string;
    // already numbered, newest first
    versions: NumberedVersion[];
    selectedVersionId: string | undefined;
    onSelectVersion: (versionId: string) => void;
    onClose: () => void;
}

const PackageVersionHistoryDrawer = ({ packageName, versions, selectedVersionId, onSelectVersion, onClose }: PackageVersionHistoryDrawerProps) => {
    const { t } = useTranslation();

    const listed = versions.filter((version): version is NumberedVersion & { versionId: string } => version.versionId !== undefined);

    return (
        <Drawer title={t('packages.versionHistory') ?? ''} subtitle={packageName} onClose={onClose}>
            {listed.length === 0 ? (
                <EmptyState icon="history" title={t('packages.noVersions') ?? ''} description={t('packages.noVersionsDescription') ?? ''} />
            ) : (
                <ul className="list-group">
                    {listed.map((version) => {
                        const selected = version.versionId === selectedVersionId;
                        const comment = version.comment ?? '';

                        return (
                            // App.css strips the list-group's side padding, which would put the active row's accent bar on top of the text
                            <li key={version.versionId} className={`list-group-item px-16 py-12${selected ? ' active' : ''}`}>
                                <div className="d-flex align-items-center gap-8 mb-4">
                                    <span className="font-weight-600">{version.label}</span>
                                    <PackageStatusBadge status={version.status} />
                                    {selected && <span className="text-secondary font-13">{t('packages.viewingVersion')}</span>}
                                    <div className="ml-auto">
                                        {!selected && (
                                            <Button onClick={() => onSelectVersion(version.versionId)}>
                                                {t('packages.viewVersion')}
                                                <span className="sr-only"> {version.label}</span>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <p className={`list-group-item-text mb-4${comment.length > 0 ? '' : ' text-secondary'}`}>
                                    {comment.length > 0 ? comment : t('packages.noVersionComment')}
                                </p>
                                <p className="list-group-item-text text-secondary font-13 mb-0">
                                    {t('packages.created')}: {dateUtils.formatDateAndTime(version.created)}
                                </p>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Drawer>
    );
};

export default PackageVersionHistoryDrawer;
